/**
 * RT1.0 / auto_rt 共通の送客ディスパッチ正本（処理系）。
 * UI 分岐はここではなく呼び出し側に置く。ただし clientId 特例など
 * 「送客先を決めるルール」はここに集約し、UI は predicate を参照するだけにする。
 */
import type { RtSendReturn } from '@/composables/useRealtimeTransferSend'

export type RtConsultKey = keyof RtSendReturn

export type RtConsultDispatchEntry = {
  hubEvent: string
  consultKey: RtConsultKey
}

/** 送客解決の入力。endpointKey 以外の特例もここに足していく。 */
export type RtConsultResolveInput = {
  endpointKey?: string | null
  clientId?: string | null
  permissionType?: number | null
}

/** endpointKey 空 / Common 明示時に使う consult */
const RT_COMMON_CONSULT_KEY: RtConsultKey = 'sendRealtimeTransferCommonConsult'

const RT_RECURRENT_DISPATCH: RtConsultDispatchEntry = {
  hubEvent: 'send-recurrent-consult',
  consultKey: 'sendRecurrentConsult'
}

/**
 * recurrent 送客コンテキストか（処理系ルール）。
 * UI のリード文差し替え等はこれを参照すればよく、判定ロジックはここに一本化する。
 */
export function isRtRecurrentConsultContext(input: {
  clientId?: string | null
  permissionType?: number | null
}): boolean {
  return (
    String(input.clientId || '') === 'recurrent_api' &&
    Number(input.permissionType) !== 5
  )
}

const RT_CONSULT_DISPATCH: Record<string, RtConsultDispatchEntry> = {
  sendXMile: { hubEvent: 'send-xmile-consult', consultKey: 'sendXMileConsult' },
  sendTrytApi: {
    hubEvent: 'send-tryt-api-consult',
    consultKey: 'sendTrytApiConsult'
  },
  sendWorkport: {
    hubEvent: 'send-workport-consult',
    consultKey: 'sendWorkportConsult'
  },
  sendHrcareer: {
    hubEvent: 'send-hrcareer-consult',
    consultKey: 'sendHrcareerConsult'
  },
  sendRecree: {
    hubEvent: 'send-recree-consult',
    consultKey: 'sendRecreeConsult'
  },
  sendCareerdraft: {
    hubEvent: 'send-careerdraft-consult',
    consultKey: 'sendCareerdraftConsult'
  },
  sendRozas: { hubEvent: 'send-rozas-consult', consultKey: 'sendRozasConsult' },
  sendNeocareer: {
    hubEvent: 'send-neocareer-consult',
    consultKey: 'sendNeocareerConsult'
  },
  sendRecruitMc: {
    hubEvent: 'send-recruit_mc-consult',
    consultKey: 'sendRecruitMcConsult'
  },
  adtopPdfMail: {
    hubEvent: 'send-adtop-consult',
    consultKey: 'sendAdtopConsult'
  },
  sendAgrinavi: {
    hubEvent: 'send-agrinavi-consult',
    consultKey: 'sendAgrinaviConsult'
  },
  sendAsiro: { hubEvent: 'send-asiro-consult', consultKey: 'sendAsiroConsult' },
  sendBaton: { hubEvent: 'send-baton-consult', consultKey: 'sendBatonConsult' },
  sendBridge: {
    hubEvent: 'send-bridge-consult',
    consultKey: 'sendBridgeConsult'
  },
  sendCapla: { hubEvent: 'send-capla-consult', consultKey: 'sendCaplaConsult' },
  sendDely: { hubEvent: 'send-dely-consult', consultKey: 'sendDelyConsult' },
  sendGrowtrail: {
    hubEvent: 'send-growtrail-consult',
    consultKey: 'sendGrowtrailConsult'
  },
  sendJobs919: {
    hubEvent: 'send-jobs919-consult',
    consultKey: 'sendJobs919Consult'
  },
  sendHitotech: {
    hubEvent: 'send-hitotech-consult',
    consultKey: 'sendHitotechConsult'
  },
  sendHitotechneocareer: {
    hubEvent: 'send-hitotechneocareer-consult',
    consultKey: 'sendHitotechneocareerConsult'
  },
  sendJaic: { hubEvent: 'send-jaic-consult', consultKey: 'sendJaicConsult' },
  sendKirara: {
    hubEvent: 'send-kirara-consult',
    consultKey: 'sendKiraraConsult'
  },
  sendKrasys: {
    hubEvent: 'send-krasys-consult',
    consultKey: 'sendKrasysConsult'
  },
  sendMiraiyu: {
    hubEvent: 'send-miraiyu-consult',
    consultKey: 'sendMiraiyuConsult'
  },
  sendMynaviBaite: {
    hubEvent: 'send-mynavibaite-consult',
    consultKey: 'sendMynaviBaiteConsult'
  },
  sendMynaviMs: {
    hubEvent: 'send-mynavims-consult',
    consultKey: 'sendMynaviMsConsult'
  },
  sendPeacekaigo: {
    hubEvent: 'send-peacekaigo-consult',
    consultKey: 'sendPeacekaigoConsult'
  },
  sendPersolinnovation: {
    hubEvent: 'send-persolinnovation-consult',
    consultKey: 'sendPersolinnovationConsult'
  },
  sendPleiades: {
    hubEvent: 'send-pleiades-consult',
    consultKey: 'sendPleiadesConsult'
  },
  sendReastage: {
    hubEvent: 'send-reastage-consult',
    consultKey: 'sendReastageConsult'
  },
  sendSimple: {
    hubEvent: 'send-simple-consult',
    consultKey: 'sendSimpleConsult'
  },
  sendSystemShared: {
    hubEvent: 'send-systemshared-consult',
    consultKey: 'sendSystemsharedConsult'
  },
  sendTechouse: {
    hubEvent: 'send-techouse-consult',
    consultKey: 'sendTechouseConsult'
  },
  sendTunaguba: {
    hubEvent: 'send-tunaguba-consult',
    consultKey: 'sendTunagubaConsult'
  },
  sendUsenworking: {
    hubEvent: 'send-usenworking-consult',
    consultKey: 'sendUsenworkingConsult'
  },
  sendYagish: {
    hubEvent: 'send-yagish-consult',
    consultKey: 'sendYagishConsult'
  },
  sendNewRtSystem: {
    hubEvent: 'send-new-rt-system-consult',
    consultKey: 'sendNewRtSystemConsult'
  },
  sendYagishCareer: {
    hubEvent: 'send-yagishcareer-consult',
    consultKey: 'sendYagishCareerConsult'
  },
  sendRealive: {
    hubEvent: 'send-realive-consult',
    consultKey: 'sendRealiveConsult'
  },
  sendDataRoxx: {
    hubEvent: 'send-data-roxx-consult',
    consultKey: 'sendDataRoxxConsult'
  },
  sendLinkMedley: {
    hubEvent: 'send-link-medley-consult',
    consultKey: 'sendLinkMedleyConsult'
  },
  sendBizreach: {
    hubEvent: 'send-bizreach-consult',
    consultKey: 'sendBizreachConsult'
  },
  sendRealtimeTransferAppeal: {
    hubEvent: 'send-realtimetransferappeal-consult',
    consultKey: 'sendRealtimeTransferAppealConsult'
  },
  sendRealtimeTransferCommon: {
    hubEvent: 'send-realtimetransfercommon-consult',
    consultKey: RT_COMMON_CONSULT_KEY
  }
}

function normalizeResolveInput(
  input?: string | null | RtConsultResolveInput
): RtConsultResolveInput {
  if (input == null || typeof input === 'string') {
    return { endpointKey: input }
  }
  return input
}

/**
 * 送客ディスパッチ情報を返す。
 * 1) recurrent（clientId 特例） 2) endpointKey 表 3) 未解決は null
 */
export function resolveRtConsultDispatch(
  input?: string | null | RtConsultResolveInput
): RtConsultDispatchEntry | null {
  const ctx = normalizeResolveInput(input)
  if (isRtRecurrentConsultContext(ctx)) {
    return RT_RECURRENT_DISPATCH
  }
  const key = ctx.endpointKey == null ? '' : String(ctx.endpointKey).trim()
  if (!key) return null
  return RT_CONSULT_DISPATCH[key] || null
}

/**
 * handlers から該当 consult を返す。
 * - recurrent コンテキスト → sendRecurrentConsult
 * - 空 / sendRealtimeTransferCommon → Common consult
 * - 未知 key → Common consult へフォールバック
 *   （RT1.0 の同意ボタン rtCommonConsultHubEvent と同じ方針。
 *     表に無い api_endpoint_key が来ても送客を落とさない）
 * - 表にある key だが handler 関数が無い場合のみ null（送客スキップ用）
 */
export function resolveRtConsultHandler(
  handlers: RtSendReturn,
  input?: string | null | RtConsultResolveInput
): (() => Promise<void>) | null {
  const ctx = normalizeResolveInput(input)
  if (isRtRecurrentConsultContext(ctx)) {
    const fn = handlers.sendRecurrentConsult
    return typeof fn === 'function' ? (fn as () => Promise<void>) : null
  }
  const key = ctx.endpointKey == null ? '' : String(ctx.endpointKey).trim()
  const entry =
    key && key !== 'sendRealtimeTransferCommon'
      ? RT_CONSULT_DISPATCH[key]
      : null
  if (!entry) {
    const common = handlers[RT_COMMON_CONSULT_KEY]
    return typeof common === 'function' ? common : null
  }
  const fn = handlers[entry.consultKey]
  if (typeof fn !== 'function') return null
  return fn as () => Promise<void>
}
